import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { CUSTOMER_MENU, filterGroupsByModules } from "../config/menu";
import { useApp } from "../context/AppContext";
import { useI18n } from "../i18n";
import { Avatar } from "../components/ui";
import Icon from "../components/ui/Icon";
import BrandMark from "../components/BrandMark";
import { brandingOf } from "../config/branding";
import Shell from "./Shell";

// พอร์ทัลลูกค้า — ใช้โครง icon rail + แผงเมนูย่อยเดียวกับฝั่งแอดมิน (มือถือเป็นแถบล่าง)
export default function CustomerLayout() {
  const { organization, enabledModules, profile, logout, isAdmin } = useApp();
  const { t, tx, lang, setLang, pick } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    setUserMenu(false);
  }, [location.pathname]);

  // White-label: ร้านที่มีสิทธิ์ใช้ชื่อ/โลโก้/สีของตัวเองแทน LucidCRM
  const brand = useMemo(() => brandingOf(organization), [organization]);

  const groups = useMemo(() => filterGroupsByModules(CUSTOMER_MENU, enabledModules), [enabledModules]);

  const railGroups = useMemo(() => {
    const list = groups.map((group) => ({
      id: group.group,
      title: pick(group, "group"),
      short: pick(group, "short"),
      railIcon: group.rail,
      items: group.items.map((item) => ({ ...item, label: pick(item, "label") })),
    }));
    if (isAdmin) {
      list.push({
        id: "__admin",
        title: t("nav.settings"),
        short: t("nav.settings"),
        railIcon: "settings",
        items: [{ to: "/admin", icon: "desktop", label: lang === "en" ? "Go to admin" : "ไปหน้าผู้ดูแล" }],
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, isAdmin, lang]);

  const title = useMemo(() => {
    const all = CUSTOMER_MENU.flatMap((group) => group.items);
    const found = all
      .filter((item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to)))
      .sort((a, b) => b.to.length - a.to.length)[0];
    return found ? pick(found, "label") : organization?.name;
  }, [location.pathname, organization, pick]);

  const onLogout = async () => {
    await logout();
    navigate("/auth/login");
  };

  const langBtn = (
    <button type="button" className="lx-pill" onClick={() => setLang(lang === "th" ? "en" : "th")}>
      {lang === "th" ? "TH" : "EN"}
    </button>
  );

  return (
    <>
      {userMenu ? <div className="lx-scrim" onClick={() => setUserMenu(false)} /> : null}
      <Shell
        groups={railGroups}
        style={brand.cssVars}
        brand={<BrandMark brand={brand} />}
        brandTo="/app"
        title={title}
        navLabel={tx("หมวดเมนู", "Menu categories")}
        toggleLabel={tx("เปิด/ปิดเมนูย่อย", "Toggle menu panel")}
        closeLabel={tx("ปิด", "Close")}
        topLeft={
          <span className="lx-hide-sm lx-topbar__org">
            <span className="lx-org__btn" style={{ cursor: "default" }}>
              <span className="lx-org__name">{brand.name}</span>
            </span>
            <span className="lx-topbar__slash">/</span>
          </span>
        }
        panelTop={
          <>
            <div className="lx-org">
              <span className="lx-org__btn" style={{ cursor: "default" }}>
                <Icon name="store" />
                <span className="lx-org__name">{brand.custom ? brand.name : organization?.name}</span>
              </span>
            </div>
            <div className="lx-ctx-row">{langBtn}</div>
          </>
        }
        topRight={
          <>
            <span className="lx-hide-sm">{langBtn}</span>
            <div className="lx-user">
              <button type="button" className="lx-user__btn" onClick={() => setUserMenu((v) => !v)} aria-label={profile?.fullname || profile?.email}>
                <Avatar name={profile?.fullname || profile?.email} size="sm" />
              </button>
              {userMenu ? (
                <div className="lx-menu">
                  <div className="lx-menu__who">
                    <div className="u-strong">{profile?.fullname || profile?.email}</div>
                    <div className="u-fs-12 u-muted">{profile?.email}</div>
                  </div>
                  <Link to="/app/profile">{t("nav.settings")}</Link>
                  <button type="button" className="is-danger" onClick={onLogout}>
                    {t("action.logout")}
                  </button>
                </div>
              ) : null}
            </div>
          </>
        }
      >
        <div className="customer-content">
          <Outlet />
        </div>
      </Shell>
    </>
  );
}
